import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "./UI";
import {
  Sparkles, BrainCircuit, Tv, LineChart, Radar, AlertTriangle, Newspaper, HandCoins,
  MessageSquare, Layers, PieChart, Briefcase, Users, Palette, FileText, HeartHandshake,
  Trophy, CalendarDays, QrCode, ShieldCheck, ArrowRight, Play, X, Video,
} from "lucide-react";

// Every tile in the Feature Explorer, plus an optional 30-second video
// walkthrough. `preview` can be:
//   - a direct .mp4 / .webm URL (rendered in <video>)
//   - a YouTube / Loom embed URL (rendered in <iframe>)
//   - null → falls back to the tile description in the modal ("coming soon")
const MODULES = [
  { icon: Sparkles, t: "Ask TotalReach", d: "AI Chief of Staff — reads your race + acts.", to: "/ask-totalreach", c: "cyan",
    preview: null,
    full: "A floating AI agent inside every screen that reads your live race, budget, approvals and calendar — and can create events, approve pending items, send the daily brief or export the whole chat as a briefing PDF." },
  { icon: BrainCircuit, t: "AI Assistant", d: "Plan a campaign in plain English.", to: "/ai-assistant", c: "cyan",
    preview: null,
    full: "Describe your race and goals — the AI builds the audience, geography, channel mix, budget plan and forecast, then launches with one human approval." },
  { icon: Tv, t: "CTV Campaigns", d: "Video across every streaming platform.", to: "/campaigns/ctv/new", c: "cyan",
    preview: null,
    full: "Traffic Connected TV inventory — Hulu, YouTube TV, Peacock, Paramount+ and more — with the same targeting as your digital display." },
  { icon: LineChart, t: "War Room", d: "Live performance across every channel.", to: "/war-room", c: "cyan",
    preview: null,
    full: "Real-time delivery, spend pacing, frequency and unique-household reach — cross-channel — in one live command dashboard." },
  { icon: Radar, t: "Political Intel", d: "Opposition tracker & polling.", to: "/political-intelligence", c: "fuchsia",
    preview: null,
    full: "Track every candidate in your race: polling averages, momentum arrows, ad activity, news headlines and opposition ad libraries — all in one screen." },
  { icon: AlertTriangle, t: "PAC Alerts", d: "FEC Schedule E threshold monitors.", to: "/political-intelligence", c: "fuchsia",
    preview: null,
    full: "Daily FEC snapshot; when a linked PAC crosses your $ threshold in a rolling window, you get an urgent notification and it drops into the daily brief." },
  { icon: Newspaper, t: "News Digest", d: "Race-scoped headline feed.", to: "/political-intelligence", c: "fuchsia",
    preview: null,
    full: "Google News + issue tracker per race, filtered to your candidate + opponents. Digest email daily; keyword flags for ad-mention headlines." },
  { icon: HandCoins, t: "Fundraising", d: "Donations, goals, recurring gifts.", to: "/fundraising/campaigns", c: "emerald",
    preview: null,
    full: "Stripe-powered fundraising campaigns with recurring gifts, refunds, chargeback UI and receipt emails baked in." },
  { icon: MessageSquare, t: "Text-to-Give", d: "SMS keywords → donation links.", to: "/fundraising/sms", c: "emerald",
    preview: null,
    full: "Publish keywords like GIVE — texters get a personalized donation link back in seconds. Twilio-powered with a dry-run pipe tester." },
  { icon: Layers, t: "Microsites", d: "Public /m/ landing pages.", to: "/fundraising/microsites", c: "emerald",
    preview: null,
    full: "AI-generated, brand-consistent landing pages for donations, events and pledges — publish under /m/ in one click." },
  { icon: PieChart, t: "Donor CRM", d: "Refunds, exports, per-donor history.", to: "/fundraising/donors", c: "emerald",
    preview: null,
    full: "Every donor's full history, CSV exports, one-click refunds, chargeback management — no external CRM required." },
  { icon: Briefcase, t: "Agency Portfolio", d: "One dashboard for every client.", to: "/agency", c: "cyan",
    preview: null,
    full: "Consolidated stats across every campaign you manage — totals, upcoming elections, pending approvals, AI cost per month." },
  { icon: Users, t: "Agency Team", d: "Bulk cross-entity role invites.", to: "/agency/team", c: "cyan",
    preview: null,
    full: "Bulk-invite consultants to N campaigns with per-entity roles in one email. Team matrix shows exactly who has access to what." },
  { icon: Palette, t: "Brand Library", d: "Tagline, logo, key issues.", to: "/brand-library", c: "cyan",
    preview: null,
    full: "One tagline, slogan set, key issues, logo pack — the AI Chief of Staff and Ad Studio Refresh always sound like you." },
  { icon: FileText, t: "Weekly Brief", d: "Auto-generated exec PDF.", to: "/briefs", c: "cyan",
    preview: null,
    full: "Weekly performance briefs — spend pacing, race intelligence, fundraising, approvals — as a branded PDF you can email to the candidate." },
  { icon: HeartHandshake, t: "Volunteers", d: "Roster + shift scheduling.", to: "/volunteers", c: "amber",
    preview: null,
    full: "Public portal for supporters to claim shifts, RSVP to events and log opposition ad sightings from their phones." },
  { icon: Trophy, t: "Leaderboard", d: "Gamified points + 5 tiers.", to: "/volunteers/leaderboard", c: "amber",
    preview: null,
    full: "Points auto-award from shifts, RSVPs, check-ins and ad sightings — 5-tier ranks (Newcomer → Ambassador). Turns your volunteer base into a retention loop." },
  { icon: CalendarDays, t: "Events", d: "RSVP + check-in + shifts.", to: "/events", c: "amber",
    preview: null,
    full: "Publish rallies, town halls or fundraisers with a public /m/ URL. RSVP + shift signup + check-in tracking all built in." },
  { icon: QrCode, t: "QR Codes", d: "Trackable attribution links.", to: "/qr", c: "amber",
    preview: null,
    full: "Mailer, yard sign and podium QR codes generate unique attribution IDs — every scan reports back into fundraising source economics." },
  { icon: ShieldCheck, t: "Approvals", d: "Multi-step budget gates.", to: "/approvals", c: "cyan",
    preview: null,
    full: "Dynamic approval chains that collapse or escalate based on budget thresholds. Full audit trail on every change." },
];

const TONES = {
  cyan: "border-cyan-400/20 hover:border-cyan-400/50 hover:bg-cyan-400/[0.04] text-cyan-300",
  fuchsia: "border-fuchsia-400/20 hover:border-fuchsia-400/50 hover:bg-fuchsia-400/[0.04] text-fuchsia-300",
  emerald: "border-emerald-400/20 hover:border-emerald-400/50 hover:bg-emerald-400/[0.04] text-emerald-300",
  amber: "border-amber-400/20 hover:border-amber-400/50 hover:bg-amber-400/[0.04] text-amber-300",
};

const TONE_ACCENTS = {
  cyan: "text-cyan-300",
  fuchsia: "text-fuchsia-300",
  emerald: "text-emerald-300",
  amber: "text-amber-300",
};

export function FeatureExplorer() {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null); // module object or null

  const openPreview = (e, m) => {
    e.stopPropagation();
    setPreview(m);
  };

  return (
    <Card data-testid="feature-explorer">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium">Feature Explorer</div>
          <h3 className="font-display text-lg font-bold text-white mt-1">Every module in your login</h3>
          <p className="text-xs text-slate-500 mt-1">Click a tile to open the module — hover the play icon for a 30-second video walkthrough.</p>
        </div>
        <button onClick={() => navigate("/onboarding/checklist")} data-testid="explorer-checklist-link"
          className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
          View launch checklist <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-2" data-testid="explorer-grid">
        {MODULES.map((m) => (
          <div key={m.t}
               data-testid={`explorer-tile-${m.t.replace(/\s+/g, "-").toLowerCase()}`}
               className={`group relative text-left rounded-lg border ${TONES[m.c]} bg-white/[0.01] p-3 transition-colors cursor-pointer`}
               onClick={() => navigate(m.to)}
               role="button"
               tabIndex={0}
               onKeyDown={(e) => { if (e.key === "Enter") navigate(m.to); }}>
            <m.icon className="w-4 h-4" />
            <div className="text-xs font-semibold text-slate-100 mt-2 leading-tight">{m.t}</div>
            <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{m.d}</div>
            <button
              onClick={(e) => openPreview(e, m)}
              data-testid={`explorer-preview-btn-${m.t.replace(/\s+/g, "-").toLowerCase()}`}
              title="Watch 30-second walkthrough"
              className={`absolute top-1.5 right-1.5 rounded-full p-1.5 bg-slate-900/70 border border-white/10 ${TONE_ACCENTS[m.c]} opacity-0 group-hover:opacity-100 hover:bg-slate-950 transition-opacity`}>
              <Play className="w-3 h-3 fill-current" />
            </button>
          </div>
        ))}
      </div>

      {preview && <VideoWalkthroughModal module={preview} onClose={() => setPreview(null)} onOpen={() => { navigate(preview.to); setPreview(null); }} />}
    </Card>
  );
}

function VideoWalkthroughModal({ module: m, onClose, onOpen }) {
  const isMp4 = m.preview && (m.preview.endsWith(".mp4") || m.preview.endsWith(".webm"));
  const isEmbed = m.preview && (m.preview.includes("loom.com") || m.preview.includes("youtube.com") || m.preview.includes("youtube-nocookie.com") || m.preview.includes("vimeo.com"));

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="video-walkthrough-modal"
         onClick={onClose}>
      <div className="bg-slate-950 border border-white/10 rounded-2xl w-full max-w-3xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className={`text-[10px] uppercase tracking-wider ${TONE_ACCENTS[m.c]}`}>Module Walkthrough</div>
            <h3 className="font-display text-lg font-bold text-white mt-0.5 flex items-center gap-2">
              <m.icon className={`w-4 h-4 ${TONE_ACCENTS[m.c]}`} /> {m.t}
            </h3>
          </div>
          <button onClick={onClose} data-testid="video-walkthrough-close" className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Video area */}
          <div className="aspect-video rounded-lg overflow-hidden bg-slate-900/60 border border-white/5 flex items-center justify-center relative">
            {isMp4 ? (
              <video src={m.preview} autoPlay controls playsInline
                data-testid="video-walkthrough-player"
                className="w-full h-full object-cover">
                <track kind="captions" />
                Your browser does not support HTML5 video.
              </video>
            ) : isEmbed ? (
              <iframe src={m.preview}
                data-testid="video-walkthrough-embed"
                title={`${m.t} walkthrough`}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen />
            ) : (
              // No video wired yet — friendly fallback
              <div className="text-center px-8" data-testid="video-walkthrough-fallback">
                <div className={`w-14 h-14 mx-auto rounded-full bg-slate-800 flex items-center justify-center border border-white/10 ${TONE_ACCENTS[m.c]}`}>
                  <Video className="w-6 h-6" />
                </div>
                <div className="text-sm text-slate-300 font-semibold mt-3">Video walkthrough coming soon</div>
                <div className="text-xs text-slate-500 mt-1">We&apos;re still recording this one. In the meantime, click <b>Open module</b> to see it live.</div>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">What it does</div>
            <p className="text-sm text-slate-200 leading-relaxed" data-testid="video-walkthrough-description">{m.full}</p>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-white/5">
            <button onClick={onClose} className="text-sm text-slate-400 hover:text-white px-3 py-2">Close</button>
            <button onClick={onOpen}
              data-testid="video-walkthrough-open-btn"
              className={`text-sm px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold inline-flex items-center gap-1.5`}>
              Open module <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
